import { IDENTITY_ADJECTIVE, IDENTITY_ANIMAL } from '~/static/identities'
import { IMedia, IUser, Media, Reaction, User } from './index'

export const ConversationRequests = ['vibe', 'message', 'friend'] as const
export const MessageTypes = [ ...ConversationRequests, '', 'match', 'welcome', 'event-created', 'invite', 'intentions' ] as const
export const MessageStatus = [ '' , 'deleted', 'modified' ] as const
export const ConversationVisibility = ['visible', 'archived', 'deleted'] as const
export interface IConversationMessage {
  _id: string
  id: string
  content: string
  conversation: string
  reactions: Reaction[]
  attachments?: {
    gif?: string
    event?: string
    link?: string
    query?: { [key: string]: string }
    place?: string
    group?: string
    token?: string
    users?: string[]
    audio?: IMedia
    medias?: IMedia[]
  }
  type: typeof MessageTypes[number]
  status: typeof MessageStatus[number]

  owner: string
  parent?: string
  target?: string

  history: {
    content: string
    createdAt: Date
  }[]

  isParent: boolean

  createdAt: Date
}

export interface ConversationMessage extends IConversationMessage {}

export class ConversationMessage implements IConversationMessage {
  parent: string

  attachments: {
    gif?: string
    event?: string
    link?: string
    query?: { [key: string]: string }
    place?: string
    group?: string
    token?: string
    users?: string[]
    audio?: Media
    medias?: Media[]
  }

  constructor(props: IConversationMessage) {

    Object.assign(this, {
      ...props,
      parent: (props.parent as any)?._id ?? props.parent,
      createdAt: new Date(props.createdAt),
      attachments: {
        ...props.attachments,
        audio: props.attachments?.audio ? new Media(props.attachments.audio) : null,
        medias: (props.attachments?.medias ?? []).map(m => new Media(m))
      }
    })
  }

  public get editedAt () {
    return (this.history ?? []).length > 0 && this.history[this.history.length - 1]?.createdAt ? this.history[this.history.length - 1]?.createdAt : null
  }
}

export const ConversationTypes = ['users', 'group', 'cohort', 'event', 'bot'] as const

export type ConversationIdentity = {
  _id: string
  animal: keyof typeof IDENTITY_ANIMAL
  adjective: keyof typeof IDENTITY_ADJECTIVE
}
export interface IConversation {
  _id: string
  id: string

  title?: string
  
  type: typeof ConversationTypes[number]
  visibility: typeof ConversationVisibility[number]
  group?: string
  event?: string

  lastMessage: string
  lastMessageDate: Date

  lastMessageOwner?: IUser

  messageCount: number
  readBy: string[]

  isAnonymous: boolean
  identities: ConversationIdentity[]
  
  requests: {
    type: (typeof ConversationRequests[number])
    owner: string
  }[]

  consents: string[]
  users: string[]
  invited: string[]
  archivedBy: string[]

  createdAt: Date
  ['@lastMessage']: IConversationMessage | null
}

export interface Conversation extends IConversation {}

export class Conversation implements IConversation {
  ['@lastMessage']: ConversationMessage | null
  
  constructor(props: IConversation) {

    Object.assign(this, {
      ...props,
      createdAt: new Date(props.createdAt),
      ['@lastMessage']: props['@lastMessage'] ? new ConversationMessage(props['@lastMessage']) : null,
      lastMessageOwner: props.lastMessageOwner ? new User(props.lastMessageOwner) : null,
    })
  }

  public isInvited (userId?: string) {
    return this.invited && userId && (this.invited.includes(userId) && !this.users.includes(userId))
  }

  public isArchived (userId?: string) {
    return this.archivedBy && userId && this.archivedBy.includes(userId)
  }

  public isParticipant (userId?: string) {
    if (!userId) return false

    return (this.invited ?? []).includes(userId) || (this.users ?? []).includes(userId)
  }

  public get isActive () {
    return this.users.length === 2
  }

  public get isRead () {
    return this.type === 'users' && this.readBy.length >= (this.users.length + this.invited.length)
  }

  public getRequest (
    requestType: typeof ConversationRequests[number],
    userId?: string
  ) {
    return this.requests.find(r => r.type === requestType && r.owner === userId)
  }
}